// ProviderProfile.js — reviews gated to completed bookings only + live re-fetch after submit
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import 'react-calendar/dist/Calendar.css';
import '../App.css';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';
const DEFAULT_SLOT_DURATION = 30;
const DEFAULT_HOURS = { start: '09:00', end: '17:00' };

function generateSlots(date, start, end, duration) {
  const slots = [];
  let current = dayjs(`${date} ${start}`);
  const endTime = dayjs(`${date} ${end}`);
  while (current.isBefore(endTime)) {
    slots.push(current.format('h:mm A'));
    current = current.add(duration, 'minute');
  }
  return slots;
}

function StarRating({ value, onChange, size = '1.4rem', readonly = false }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '0.15rem' }}>
      {[1,2,3,4,5].map(star => (
        <span key={star}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          style={{ fontSize: size, cursor: readonly ? 'default' : 'pointer', color: star <= (hovered || value) ? '#d97706' : '#ddd8ce', transition: 'color 0.15s', lineHeight: 1 }}
        >★</span>
      ))}
    </div>
  );
}

function ProviderProfile() {
  const { providerId } = useParams();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [activeService, setActiveService] = useState(null);
  const [bookingService, setBookingService] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [messageSending, setMessageSending] = useState(false);

  const [subscribeMonthly, setSubscribeMonthly] = useState(false);
  const [ratings, setRatings] = useState({});
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [previewMedia, setPreviewMedia] = useState(null);

  // ── Track which services the client has COMPLETED bookings for ────────────
  const [completedServiceNames, setCompletedServiceNames] = useState(new Set());

  // ── Deals state ───────────────────────────────────────────────────────────
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    getCurrentUser().then(async u => {
      setUserId(u.userId);
      try {
        const attrs = await fetchUserAttributes();
        setUserName(attrs.name || attrs.email?.split('@')[0] || '');
        setUserEmail(attrs.email || '');
      } catch {}
      // Fetch client's completed bookings for this provider
      fetchCompletedBookings(u.userId);
    }).catch(() => {});
    fetchProvider();
  }, [providerId]);

  useEffect(() => {
    if (!provider) return;
    fetch(`${API_URL}/bookings?providerId=${provider.providerId}`)
      .then(r => r.json()).then(d => setBookedSlots(Array.isArray(d) ? d : [])).catch(() => {});
  }, [provider]);

  useEffect(() => {
    if (!providerId) return;
    fetch(`${API_URL}/deals/provider?providerId=${providerId}`)
      .then(r => r.json())
      .then(data => {
        const today = new Date().toISOString().split('T')[0];
        const active = (Array.isArray(data) ? data : []).filter(
          d => d.active && (!d.expiryDate || d.expiryDate >= today)
        );
        setDeals(active);
      })
      .catch(() => {});
  }, [providerId]);

  useEffect(() => {
    if (bookingService?.recurring) {
      setSubscribeMonthly(true);
    } else {
      setSubscribeMonthly(false);
    }
  }, [bookingService]);

  useEffect(() => {
    if (bookingService && userId) {
      fetch(`${API_URL}/client-bookings?clientId=${userId}`)
        .then(r => r.json())
        .then(data => {
          window._providerClientBookings = (Array.isArray(data) ? data : [])
            .filter(b => b.providerId === providerId);
        })
        .catch(() => { window._providerClientBookings = []; });
    }
  }, [bookingService, userId, providerId]);

  // ── Check which services this client has completed ────────────────────────
  const fetchCompletedBookings = async (uid) => {
    try {
      const res = await fetch(`${API_URL}/client-bookings?clientId=${uid}`);
      const data = await res.json();
      const today = new Date().toISOString().split('T')[0];
      const completed = new Set(
        (Array.isArray(data) ? data : [])
          .filter(b =>
            b.providerId === providerId &&
            b.status !== 'cancelled' &&
            // either marked complete by provider OR the date has passed
            (b.completionStatus === 'completed' || b.date < today)
          )
          .map(b => b.service?.name)
          .filter(Boolean)
      );
      setCompletedServiceNames(completed);
    } catch {}
  };

  const fetchProvider = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/providers?providerId=${providerId}`);
      const data = await res.json();
      setProvider(data);
      if (data.services?.length > 0) setActiveService(data.services[0]);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Re-fetch provider after rating to show new review immediately
  const refreshProvider = async () => {
    try {
      const res = await fetch(`${API_URL}/providers?providerId=${providerId}`);
      const data = await res.json();
      setProvider(data);
      // keep activeService in sync
      if (activeService) {
        const updated = (data.services || []).find(s => s.name === activeService.name);
        if (updated) setActiveService(updated);
      }
    } catch {}
  };

  const handleRatingChange = (serviceId, field, value) => {
    setRatings(prev => ({ ...prev, [serviceId]: { ...prev[serviceId], [field]: value } }));
  };

  const handleSubmitRating = async (svc) => {
    const svcId = svc.id?.toString() || svc.name;
    const r = ratings[svcId] || {};
    if (!r.star) return;
    handleRatingChange(svcId, 'submitting', true);
    try {
      const res = await fetch(`${API_URL}/rate-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId, serviceId: svcId, clientId: userId, rating: r.star, review: r.review || '' }),
      });
      const data = await res.json();
      if (res.ok) {
        handleRatingChange(svcId, 'submitted', true);
        handleRatingChange(svcId, 'submitting', false);
        // Re-fetch provider so ratings appear immediately on the profile
        await refreshProvider();
        setTimeout(() => handleRatingChange(svcId, 'submitted', false), 3000);
      }
    } catch (err) { handleRatingChange(svcId, 'submitting', false); }
  };

  const handleMessageProvider = async () => {
    if (!userId || !providerId || userId === providerId) return;
    setMessageSending(true);
    try {
      await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderId: userId, recipientId: providerId,
          senderName: userName || 'Client', recipientName: provider.name,
          senderType: 'client',
          text: `Hi ${provider.name}! I had a question about your services.`,
          recipientEmail: provider.email || '',
        }),
      });
    } catch {}
    setMessageSending(false);
    navigate('/client-dashboard?tab=inbox');
  };

  const getAvailableSlots = () => {
    if (!provider || !bookingService) return [];
    const avail = provider.availability || {};
    const wh = avail.workingHours || DEFAULT_HOURS;
    const dur = avail.slotDuration || DEFAULT_SLOT_DURATION;
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    if ((avail.blockedDates || []).includes(dateStr)) return [];
    const blocked = (avail.blockedSlots || {})[dateStr] || [];
    const all = generateSlots(dateStr, wh.start, wh.end, dur);
    const isToday = dateStr === dayjs().format('YYYY-MM-DD');
    return all.filter(s => {
      if (blocked.includes(s)) return false;
      if (isToday) { const t = dayjs(`${dateStr} ${s}`, 'YYYY-MM-DD h:mm A'); if (t.isBefore(dayjs())) return false; }
      return true;
    });
  };

  const isSlotBooked = (slot) => {
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    return bookedSlots.some(b => b.date === dateStr && b.slot === slot && b.paymentStatus !== 'pending' && b.status !== 'cancelled');
  };

  const tileDisabled = ({ date }) => {
    const avail = provider?.availability || {};
    const d = dayjs(date).format('YYYY-MM-DD');
    return (avail.blockedDates || []).includes(d) || dayjs(date).isBefore(dayjs(), 'day');
  };

  const handleBookNow = async () => {
    if (!bookingService || !selectedSlot) return;
    setBookingLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      let duplicate = null;
      try {
        const checkRes = await fetch(`${API_URL}/client-bookings?clientId=${userId}`);
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          duplicate = (Array.isArray(checkData) ? checkData : []).find(b =>
            b.providerId === providerId && b.status !== 'cancelled' &&
            b.service?.name === bookingService?.name &&
            (b.type === 'subscription' || b.date >= today)
          );
        }
      } catch {}
      if (duplicate) {
        setBookingLoading(false);
        alert('Appointment already booked! You have an upcoming ' + bookingService?.name + (duplicate.date ? ' on ' + duplicate.date + ' at ' + duplicate.slot : '') + '. Please cancel or reschedule it first.');
        return;
      }
      const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
      const res = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId, clientId: userId, date: dateStr, slot: selectedSlot,
          service: bookingService, providerName: provider.name,
          subscribeMonthly: bookingService.recurring ? subscribeMonthly : false,
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { alert('Something went wrong.'); }
    setBookingLoading(false);
  };

  const handlePayInPerson = async () => {
    if (!bookingService || !selectedSlot) return;
    setBookingLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      let duplicate = null;
      try {
        const checkRes = await fetch(`${API_URL}/client-bookings?clientId=${userId}`);
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          duplicate = (Array.isArray(checkData) ? checkData : []).find(b =>
            b.providerId === providerId && b.status !== 'cancelled' &&
            b.service?.name === bookingService?.name &&
            (b.type === 'subscription' || b.date >= today)
          );
        }
      } catch {}
      if (duplicate) {
        setBookingLoading(false);
        alert('Appointment already booked! Please cancel your existing appointment first.');
        return;
      }
      const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
      const res = await fetch(`${API_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId, clientId: userId, date: dateStr, slot: selectedSlot,
          service: bookingService, status: 'confirmed', paymentStatus: 'pay_in_person',
          subscribeMonthly: bookingService.recurring ? subscribeMonthly : false,
          clientName: userName, clientEmail: userEmail,
        }),
      });
      const data = await res.json();
      if (res.status === 409) { alert(data.error || 'Appointment already booked!'); setBookingLoading(false); return; }
      if (!res.ok) { alert('Something went wrong: ' + (data.error || res.status)); setBookingLoading(false); return; }
      navigate('/booking-success?tab=bookings');
    } catch (err) { alert('Something went wrong: ' + err.message); }
    setBookingLoading(false);
  };

  const intervalLabel = (interval) => {
    if (interval === 'week') return 'Weekly';
    if (interval === 'biweek') return 'Bi-weekly';
    return 'Monthly';
  };

  const serviceTypeIcon = (type) => {
    if (!type) return '✦';
    const t = type.toLowerCase();
    if (t.includes('barber') || t.includes('hair')) return '✂';
    if (t.includes('tattoo')) return '◆';
    if (t.includes('detail') || t.includes('auto')) return '◉';
    if (t.includes('nail')) return '✦';
    if (t.includes('massage')) return '◈';
    return '✦';
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid var(--green)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading profile...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!provider || !provider.name) return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: '2rem' }}>😕</p>
      <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'var(--text)', fontSize: '1.2rem' }}>Provider not found</p>
      <button className="btn-primary" onClick={() => navigate('/client-dashboard')}>Browse Providers →</button>
    </div>
  );

  const oneTimeServices = provider.services?.filter(s => !s.recurring) || [];
  const allReviews = (provider.services || []).flatMap(s =>
    (s.ratings || []).filter(r => r.review).map(r => ({ ...r, serviceName: s.name }))
  ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // ── Rating section for a service ─────────────────────────────────────────
  const renderRatingSection = (svc) => {
    if (!userId || userId === providerId) return null;
    const svcId = svc.id?.toString() || svc.name;
    const r = ratings[svcId] || {};
    const existing = (svc.ratings || []).find(rt => rt.clientId === userId);
    const canReview = completedServiceNames.has(svc.name);

    if (!canReview) {
      return (
        <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔒</span>
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.2rem' }}>Review after your appointment</p>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              You can leave a review once you've had a completed booking for {svc.name}.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={{ background: 'var(--cream-dark)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
        <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.875rem' }}>
          {existing ? 'Update your rating' : 'Rate this service'}
        </p>
        <StarRating value={r.star || existing?.rating || 0} onChange={(v) => handleRatingChange(svcId, 'star', v)} size="1.75rem" />
        {(r.star || existing?.rating) && (
          <div style={{ marginTop: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <textarea
              value={r.review !== undefined ? r.review : (existing?.review || '')}
              onChange={e => handleRatingChange(svcId, 'review', e.target.value)}
              placeholder="Share your experience (optional)..."
              rows={3}
              style={{ width: '100%', background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: '9px', padding: '0.75rem 1rem', color: 'var(--text)', fontSize: '0.875rem', outline: 'none', resize: 'vertical', fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = 'var(--green)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            {r.submitted
              ? <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--green)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem' }}>✓</span>
                  Rating saved!
                </span>
              : <button
                  onClick={() => handleSubmitRating(svc)}
                  disabled={r.submitting}
                  style={{ alignSelf: 'flex-start', background: 'var(--green)', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', opacity: r.submitting ? 0.7 : 1 }}
                >{r.submitting ? 'Saving...' : existing ? 'Update Rating' : 'Submit Rating'}</button>
            }
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {previewMedia && (
        <div onClick={() => setPreviewMedia(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, cursor: 'zoom-out' }}>
          <img src={previewMedia} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '12px' }} />
          <button onClick={() => setPreviewMedia(null)} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: 'white', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* ── Booking modal ── */}
      {bookingService && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,24,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', animation: 'fadeIn 0.2s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--border)' }}>
              <div>
                <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.2rem' }}>{bookingService.name}</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  with {provider.name} · <strong style={{ color: 'var(--green)' }}>${bookingService.price}{subscribeMonthly && bookingService.recurring ? '/month' : ''}</strong> · {bookingService.duration} min
                </p>
              </div>
              <button onClick={() => { setBookingService(null); setSelectedSlot(null); }} style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.82rem', padding: '0.35rem 0.75rem', fontFamily: 'DM Sans, sans-serif' }}>✕ Close</button>
            </div>

            {bookingService.recurring && (
              <div style={{ background: 'rgba(26,92,58,0.05)', border: '1px solid rgba(26,92,58,0.18)', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.1rem' }}>Subscribe {intervalLabel(bookingService.interval)}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Auto-book every {bookingService.interval === 'biweek' ? '2 weeks' : bookingService.interval} · cancel anytime</p>
                  </div>
                  <div onClick={() => setSubscribeMonthly(v => !v)} style={{ width: 44, height: 24, borderRadius: '99px', background: subscribeMonthly ? 'var(--green)' : 'var(--border-dark)', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 3, left: subscribeMonthly ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                </div>
              </div>
            )}

            <p style={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Pick a Date</p>
            <div style={{ marginBottom: '1.5rem' }}>
              <Calendar onChange={d => { setSelectedDate(d); setSelectedSlot(null); }} value={selectedDate} tileDisabled={tileDisabled} minDate={new Date()} />
            </div>

            <p style={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Pick a Time · {dayjs(selectedDate).format('MMM D, YYYY')}</p>
            {getAvailableSlots().length === 0
              ? <p style={{ color: '#dc2626', marginBottom: '1.5rem', fontSize: '0.875rem' }}>No available slots on this day.</p>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {getAvailableSlots().map(slot => {
                    const isBooked = isSlotBooked(slot);
                    return (
                      <button key={slot} onClick={() => { if (!isBooked) setSelectedSlot(slot); }} disabled={isBooked}
                        style={{ background: isBooked ? 'var(--cream-mid)' : selectedSlot === slot ? 'var(--green)' : 'var(--green-pale)', border: `1.5px solid ${isBooked ? 'var(--border)' : selectedSlot === slot ? 'var(--green)' : 'rgba(26,92,58,0.25)'}`, color: isBooked ? 'var(--text-light)' : selectedSlot === slot ? 'white' : 'var(--green)', borderRadius: '8px', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: isBooked ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                      >{slot}</button>
                    );
                  })}
                </div>
            }

            {selectedSlot && (
              <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.2)', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
                <p style={{ fontSize: '0.68rem', color: 'var(--green)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Booking Summary</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.2rem' }}>
                  <span style={{ color: 'var(--text-mid)' }}>{bookingService.name} with {provider.name}</span>
                  <span style={{ fontWeight: 800, fontFamily: 'Playfair Display, serif', color: 'var(--text)' }}>${bookingService.price}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{dayjs(selectedDate).format('MMM D, YYYY')} at {selectedSlot}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{bookingService.duration} min</span>
                </div>
              </div>
            )}

            {(() => {
              const today = new Date().toISOString().split('T')[0];
              const existingForService = (window._providerClientBookings || []).find(b =>
                b.service?.name === bookingService?.name && b.status !== 'cancelled' &&
                (b.type === 'subscription' || b.date >= today)
              );
              if (existingForService) {
                return (
                  <>
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.25rem' }}>⚠️ Already Booked</p>
                      <p style={{ fontSize: '0.78rem', color: '#991b1b', lineHeight: 1.5 }}>You already have an upcoming {existingForService.service?.name}. Cancel it first.</p>
                    </div>
                    <button className="btn-secondary" style={{ width: '100%' }} onClick={() => { setBookingService(null); setSelectedSlot(null); }}>Close</button>
                  </>
                );
              }
              return (
                <>
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <button className="btn-secondary" style={{ flex: 1 }} onClick={() => { setBookingService(null); setSelectedSlot(null); }}>Cancel</button>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={handleBookNow} disabled={!selectedSlot || bookingLoading}>
                      {bookingLoading ? 'Loading...' : subscribeMonthly && bookingService.recurring ? 'Subscribe →' : 'Checkout →'}
                    </button>
                  </div>
                  <button onClick={handlePayInPerson} disabled={!selectedSlot || bookingLoading}
                    style={{ width: '100%', padding: '0.75rem', background: 'transparent', border: '1.5px solid rgba(26,92,58,0.3)', borderRadius: '10px', color: 'var(--green)', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'DM Sans, sans-serif' }}>
                    Pay in Person Instead
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <div style={{ background: 'var(--green)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(74,158,107,0.35) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 800, color: 'white', cursor: 'pointer' }} onClick={() => navigate('/')}>Vyb<span style={{ color: '#a8dfb8' }}>lo</span></div>
          <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '8px', padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>← Back to Browse</button>
        </div>
        <div style={{ padding: '2.5rem 2rem 3rem', position: 'relative', zIndex: 1, maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '2rem', alignItems: 'center' }}>
          <div style={{ width: '120px', height: '120px', borderRadius: '20px', overflow: 'hidden', border: '3px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', flexShrink: 0 }}>
            {provider.profilePicUrl ? <img src={provider.profilePicUrl} alt={provider.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ opacity: 0.7 }}>{serviceTypeIcon(provider.serviceType)}</span>}
          </div>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.75rem', marginBottom: '0.625rem', letterSpacing: '0.05em' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 5px #4ade80' }} />AVAILABLE TO BOOK
            </div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 800, color: 'white', lineHeight: 1.1, marginBottom: '0.4rem' }}>{provider.name}</h1>
            <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              {[...new Set((provider.services || []).map(s => s.serviceType).filter(Boolean))].join(' · ') || provider.serviceType}
              {provider.location ? ` · 📍 ${provider.location}` : ''}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <StarRating value={Math.round(provider.overallRating || 0)} readonly size="1.1rem" />
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', fontWeight: 600 }}>
                {provider.overallRating ? `${provider.overallRating} · ${provider.totalRatingCount} review${provider.totalRatingCount !== 1 ? 's' : ''}` : 'No reviews yet'}
              </span>
              {provider.verified && (
                <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 800, padding: '0.15rem 0.6rem', letterSpacing: '0.03em' }}>✓ Verified</span>
              )}
            </div>
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <button
              onClick={() => { setBookingService(oneTimeServices[0] || provider.services?.[0]); setSelectedSlot(null); }}
              style={{ background: 'white', color: 'var(--green)', border: 'none', borderRadius: '12px', padding: '0.875rem 2rem', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >Book Now →</button>
            {userId && userId !== providerId && (
              <button
                onClick={handleMessageProvider}
                disabled={messageSending}
                style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px', padding: '0.6rem 2rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', opacity: messageSending ? 0.7 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >{messageSending ? 'Opening...' : '💬 Message'}</button>
            )}
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textAlign: 'center' }}>
              from ${Math.min(...(provider.services || [{ price: 0 }]).map(s => parseFloat(s.price || 0)))}
            </p>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 340px', gap: '2rem', alignItems: 'start' }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

          {/* Deals */}
          {deals.length > 0 && (
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>🏷️ Current Deals</p>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{deals.length} active offer{deals.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {deals.map(deal => {
                  const discountedPrice = deal.originalPrice
                    ? deal.discountType === 'percent'
                      ? (deal.originalPrice * (1 - deal.discountValue / 100)).toFixed(2)
                      : Math.max(0, deal.originalPrice - deal.discountValue).toFixed(2)
                    : null;
                  const expiringSoon = deal.expiryDate && (new Date(deal.expiryDate) - new Date()) < 3 * 24 * 60 * 60 * 1000;
                  return (
                    <div key={deal.dealId} style={{ background: 'linear-gradient(135deg, #1a5c3a 0%, #2d7a52 100%)', borderRadius: '14px', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ background: '#f5c842', color: '#1a1a1a', padding: '3px 10px', borderRadius: 50, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em' }}>
                            {deal.discountType === 'percent' ? `${deal.discountValue}% OFF` : `$${deal.discountValue} OFF`}
                          </span>
                          {expiringSoon && <span style={{ background: 'rgba(220,38,38,0.85)', color: 'white', padding: '3px 8px', borderRadius: 50, fontSize: 11, fontWeight: 700 }}>⏰ Ends soon</span>}
                        </div>
                        <p style={{ color: 'white', fontWeight: 700, fontSize: 16, margin: '0 0 4px', fontFamily: 'Playfair Display, serif' }}>{deal.title}</p>
                        {deal.description && <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: '0 0 6px', lineHeight: 1.4 }}>{deal.description}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          {discountedPrice && (
                            <span style={{ color: '#f5c842', fontWeight: 800, fontSize: 18 }}>
                              ${discountedPrice}
                              {deal.originalPrice && <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400, fontSize: 14, textDecoration: 'line-through', marginLeft: 8 }}>${deal.originalPrice}</span>}
                            </span>
                          )}
                          {deal.serviceName && <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>· {deal.serviceName}</span>}
                          {deal.expiryDate && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Expires {new Date(deal.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                        </div>
                      </div>
                      <button onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })} style={{ padding: '10px 20px', background: 'white', color: '#1a5c3a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>Book Now</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {provider.bio && (
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.875rem' }}>About</p>
              <p style={{ color: 'var(--text-mid)', lineHeight: 1.8, fontSize: '0.95rem' }}>{provider.bio}</p>
            </div>
          )}

          {/* Services */}
          <div id="services-section" style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>Services</p>
            {provider.services?.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>
                {provider.services.map((svc, i) => (
                  <button key={i} onClick={() => setActiveService(svc)}
                    style={{ background: activeService?.name === svc.name ? 'var(--green)' : 'var(--cream)', border: `1.5px solid ${activeService?.name === svc.name ? 'var(--green)' : 'var(--border)'}`, color: activeService?.name === svc.name ? 'white' : 'var(--text-mid)', borderRadius: '8px', padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}
                  >{svc.name}</button>
                ))}
              </div>
            )}
            {activeService && (
              <div style={{ animation: 'fadeIn 0.2s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                  <div>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.25rem' }}>{activeService.name}</h3>
                    {activeService.description && <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>{activeService.description}</p>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                    <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', fontWeight: 800, color: 'var(--green)', lineHeight: 1 }}>${activeService.price}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{activeService.duration} min{activeService.recurring ? ` · per ${activeService.interval}` : ''}</p>
                  </div>
                </div>

                {(activeService.mediaUrls || []).length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Photos & Videos</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.625rem' }}>
                      {(activeService.mediaUrls || []).map((item, i) => {
                        const url = typeof item === 'string' ? item : item.url;
                        const isVideo = typeof item === 'object' ? item.type === 'video' : url.match(/\.(mp4|mov|webm)/i);
                        return (
                          <div key={i} onClick={() => !isVideo && setPreviewMedia(url)}
                            style={{ aspectRatio: '1', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--cream)', cursor: isVideo ? 'default' : 'zoom-in', position: 'relative', transition: 'all 0.2s' }}
                            onMouseEnter={e => { if (!isVideo) { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                          >
                            {isVideo
                              ? <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline controls onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} />
                              : <img src={url} alt={`${activeService.name} ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            }
                            {isVideo && <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(0,0,0,0.55)', color: 'white', borderRadius: '5px', fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px' }}>▶ VIDEO</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ background: 'var(--cream)', borderRadius: '12px', padding: '1.25rem', border: '1px solid var(--border)', marginBottom: '1.25rem' }}>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>What to expect</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {[`${activeService.duration} min session`, `$${activeService.price} per visit`, 'Instant booking confirmation', 'Pay online or in person'].map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', color: 'white', flexShrink: 0 }}>✓</div>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-mid)' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rating summary */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <StarRating value={Math.round(activeService.avgRating || 0)} readonly size="1.1rem" />
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {activeService.avgRating ? `${activeService.avgRating} · ${activeService.ratingCount} rating${activeService.ratingCount !== 1 ? 's' : ''}` : 'No ratings yet'}
                  </span>
                </div>

                {/* Existing reviews */}
                {(activeService.ratings || []).filter(r => r.review).length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Reviews</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                      {(activeService.ratings || []).filter(r => r.review).slice(-4).reverse().map((r, i) => (
                        <div key={i} style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.875rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                            <StarRating value={r.rating} readonly size="0.85rem" />
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-light)' }}>{dayjs(r.createdAt).format('MMM D, YYYY')}</span>
                            {r.clientId === userId && <span style={{ fontSize: '0.65rem', color: 'var(--green)', fontWeight: 700, background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.2)', borderRadius: '5px', padding: '0.05rem 0.4rem' }}>Your review</span>}
                          </div>
                          <p style={{ fontSize: '0.82rem', color: 'var(--text-mid)', lineHeight: 1.55, fontStyle: 'italic' }}>"{r.review}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Rating section — gated to completed bookings ── */}
                {renderRatingSection(activeService)}

                <button onClick={() => { setBookingService(activeService); setSelectedSlot(null); }}
                  style={{ width: '100%', background: 'var(--green)', color: 'white', border: 'none', borderRadius: '10px', padding: '0.875rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginTop: '1.25rem', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--green-dark)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--green)'}
                >Book {activeService.name} →</button>
              </div>
            )}
          </div>

          {/* All reviews across services */}
          {allReviews.length > 0 && (
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>All Reviews</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <StarRating value={Math.round(provider.overallRating || 0)} readonly size="0.9rem" />
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-mid)' }}>{provider.overallRating || '—'}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>({provider.totalRatingCount || 0} total)</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {allReviews.slice(0, 8).map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', padding: '0.875rem', background: 'var(--cream)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--green-pale)', border: '1.5px solid rgba(26,92,58,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--green)', fontSize: '0.85rem', flexShrink: 0 }}>
                      {(r.clientId || 'A').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <StarRating value={r.rating} readonly size="0.85rem" />
                        <span style={{ fontSize: '0.68rem', color: 'var(--green)', fontWeight: 600, background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', borderRadius: '5px', padding: '0.05rem 0.45rem' }}>{r.serviceName}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-light)' }}>{dayjs(r.createdAt).format('MMM D, YYYY')}</span>
                      </div>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-mid)', lineHeight: 1.55, fontStyle: 'italic' }}>"{r.review}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '1.5rem' }}>
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '12px', overflow: 'hidden', border: '2px solid rgba(26,92,58,0.2)', background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>
                {provider.profilePicUrl ? <img src={provider.profilePicUrl} alt={provider.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : serviceTypeIcon(provider.serviceType)}
              </div>
              <div>
                <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'var(--text)', fontSize: '0.95rem', marginBottom: '0.15rem' }}>{provider.name}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {[...new Set((provider.services || []).map(s => s.serviceType).filter(Boolean))].join(' · ') || provider.serviceType}
                  {provider.location ? ` · ${provider.location}` : ''}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                { label: 'Rating', value: provider.overallRating ? `★ ${provider.overallRating}` : 'No ratings yet', color: provider.overallRating ? '#d97706' : 'var(--text-light)' },
                { label: 'Services', value: `${provider.services?.length || 0} offered` },
                { label: 'Reviews', value: provider.totalRatingCount ? `${provider.totalRatingCount} reviews` : 'None yet' },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--cream)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.label}</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: row.color || 'var(--text)' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.875rem' }}>Book a Service</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(provider.services || []).map((svc, i) => {
                const thumb = (svc.mediaUrls || [])[0];
                const thumbUrl = thumb ? (typeof thumb === 'string' ? thumb : thumb.url) : null;
                return (
                  <div key={i} onClick={() => { setBookingService(svc); setSelectedSlot(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(26,92,58,0.3)'; e.currentTarget.style.background = 'var(--green-tint)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--cream)'; }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: '8px', overflow: 'hidden', background: 'var(--green-pale)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                      {thumbUrl ? <img src={thumbUrl} alt={svc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ opacity: 0.4 }}>✦</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '0.1rem' }}>{svc.name}</p>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{svc.duration} min{svc.recurring ? ` · ${intervalLabel(svc.interval)}` : ''}</p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'var(--green)', fontSize: '1rem' }}>${svc.price}</p>
                      {svc.avgRating > 0 && <p style={{ fontSize: '0.65rem', color: '#d97706' }}>★ {svc.avgRating} ({svc.ratingCount})</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {userId && userId !== providerId && (
            <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '14px', padding: '1.25rem', boxShadow: 'var(--shadow-sm)' }}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.4rem' }}>💬 Have a question?</p>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.875rem', lineHeight: 1.5 }}>Message {provider.name} directly before booking.</p>
              <button onClick={handleMessageProvider} disabled={messageSending}
                style={{ width: '100%', background: 'var(--green)', color: 'white', border: 'none', borderRadius: '9px', padding: '0.75rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', opacity: messageSending ? 0.7 : 1 }}
                onMouseEnter={e => { if (!messageSending) e.currentTarget.style.background = 'var(--green-dark)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--green)'}
              >{messageSending ? 'Opening inbox...' : 'Send a Message →'}</button>
            </div>
          )}

          {provider.location && (
            <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.15)', borderRadius: '14px', padding: '1.25rem' }}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--green)', marginBottom: '0.5rem' }}>📍 Location</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-mid)' }}>{provider.location}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProviderProfile;
